import { isEquipmentBased, SwitchField } from "common";
import { useFormikContext } from "formik";
import React from "react";
import { DisplayStepOption, Experience, Form, TicketGenerationStrategy } from "types";
import { GuestManagementFormValues } from "./GuestManagement";
import { DEFAULT_NOTIFICATIONS, NotificationsSection } from "./NotificationsSection";
import { QuestionnaireWarningModal } from "./QuestionnaireWarningModal";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useForm } from "@/hooks/questionnaireFormHooks";
import { useVisibility } from "@/hooks/useVisibility";
import { hasSecondaryDemographics } from "@/models/Experience";

interface Props {
    value: boolean;
    experience: Experience;
}

export const AttendeesSection = ({ value, experience }: Props) => {
    const [showQuestionnaireWarningModal, { toggle: toggleQuestionnaireWarningModal }] = useVisibility(false);

    const { form, globalForm } = useForm(experience.id, experience.object);
    const { values } = useFormikContext<GuestManagementFormValues>();

    const { setFieldValue } = useFormikContext<GuestManagementFormValues>();

    const isProductFormNotOrganizerOnly = form ? getIsProductFormNotOrganizerOnly(form) : false;

    const isGlobalFormNotOrganizerOnly = globalForm ? getIsGlobalFormNotOrganizerOnly(globalForm, form) : false;

    const isEquipmentWithNoDemographics = isEquipmentBased(experience) && !hasSecondaryDemographics(experience);

    const handleAttendeeUniqueChange = (value: boolean) => {
        if (isProductFormNotOrganizerOnly && !value) {
            toggleQuestionnaireWarningModal();
            return;
        }

        if (isGlobalFormNotOrganizerOnly && !value) {
            toggleQuestionnaireWarningModal();
            return;
        }

        setFieldValue("requireGuestIdentification", value);

        if (!value) {
            setFieldValue("qrCodePreference.ticketGenerationStrategy", TicketGenerationStrategy.BOOKING);

            if (values?.checkInPreference?.validations) {
                setFieldValue("checkInPreference.validations", []);
            }

            setFieldValue("notifications", DEFAULT_NOTIFICATIONS);
        }
    };

    return (
        <>
            <QuestionnaireWarningModal
                experience={experience}
                isGlobalWarning={isGlobalFormNotOrganizerOnly}
                isProductWarning={isProductFormNotOrganizerOnly}
                isOpen={showQuestionnaireWarningModal}
                onClose={toggleQuestionnaireWarningModal}
            />

            <ContentLayout.Section
                title="Attendees"
                description="Configure whether you interact only with the primary customer, or with each attendee in the reservation"
            >
                <div className="space-y-2.5">
                    <SwitchField
                        isDisabled={isEquipmentWithNoDemographics}
                        isChecked={value}
                        onChange={handleAttendeeUniqueChange}
                    >
                        Uniquely identify each attendee in the reservation
                    </SwitchField>
                </div>

                <NotificationsSection />
            </ContentLayout.Section>

            <hr className="my-6 border-gray-light" />
        </>
    );
};

const getIsProductFormNotOrganizerOnly = (form: Form): boolean => {
    if (!form) return false;

    const check = form.fields.every(
        (field) => field.organizerOnly || field.displayStep == DisplayStepOption.WAIVER_SIGNING,
    );
    return !check;
};

export const getIsGlobalFormNotOrganizerOnly = (globalForm: Form, form?: Form): boolean => {
    if (form || !globalForm) return false;

    const check = globalForm.fields?.every(
        (field) => field.organizerOnly || field.displayStep === DisplayStepOption.WAIVER_SIGNING,
    );

    return !check;
};
